import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LabelPreview from '../components/LabelPreview.jsx';
import Modal from '../components/Modal.jsx';
import { api } from '../lib/api.js';
import { BARCODE_SYMBOLOGIES, applyBarcodeSymbology, createBarcodeObject, getBarcodeDefinition, isBarcodeObject, resolveBarcodeSymbology } from '../lib/barcodes.js';
import { ZEBRA_FONTS, fontMatrix, isScalable, isStandardFont } from '../studio/zebra/ZebraFonts.js';
import { getDownloadedFont, listDownloadedFonts } from '../studio/zebra/DownloadedFonts.js';

const SCALE = 3;
const MIN_OBJECT_SIZE = 2;
const DEFAULT_GRID_SIZE = 2;
const DRAG_THRESHOLD_PX = 4;
const GUIDE_THRESHOLD_MM = 1.2;
const TOOLBOX_DRAG_MIME = 'application/x-etiquetando-toolbox-item';
const AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000; // ponytail: ajuste aqui para mudar o intervalo do autosave
const WINDOW_REGISTRY = [
    { id: 'toolbox', title: 'ToolBox' },
    { id: 'objects', title: 'Objects' },
    { id: 'formSetup', title: 'Form setup' },
    { id: 'form', title: 'Form' },
    { id: 'formProperties', title: 'Form properties' },
    { id: 'selectionProperties', title: 'Selection properties' },
    { id: 'objectProperties', title: 'Object properties' },
];
const INITIAL_WINDOW_STATE = Object.fromEntries(
    WINDOW_REGISTRY.map((windowItem) => [windowItem.id, { hidden: false, minimized: false }]),
);
const TOOLBAR_ACTIONS = [
    { id: 'save', icon: 'save', label: 'Salvar' },
    { id: 'import', icon: 'upload_file', label: 'Importar' },
    { id: 'export', icon: 'download', label: 'Exportar' },
];
const ALIGN_ACTIONS = [
    { id: 'left', icon: 'format_align_left', label: 'Left' },
    { id: 'center', icon: 'format_align_center', label: 'Center' },
    { id: 'right', icon: 'format_align_right', label: 'Right' },
    { id: 'top', icon: 'vertical_align_top', label: 'Top' },
    { id: 'middle', icon: 'vertical_align_center', label: 'Middle' },
    { id: 'bottom', icon: 'vertical_align_bottom', label: 'Bottom' },
];
const DISTRIBUTE_ACTIONS = [
    { id: 'horizontal', icon: 'horizontal_distribute', label: 'Distribuir H' },
    { id: 'vertical', icon: 'vertical_distribute', label: 'Distribuir V' },
];
const PALETTE = [
    { type: 'text', label: 'Label', icon: 'title', detail: 'Texto fixo' },
    { type: 'variable', label: 'TextBox', icon: 'view_quilt', detail: 'Campo variavel' },
    { type: 'barcode', label: 'BarCode', icon: 'barcode', detail: 'Codigo 1D' },
    { type: 'qrcode', label: 'Preview', icon: 'qr_code_2', detail: 'Codigo 2D' },
    { type: 'line', label: 'Line', icon: 'horizontal_rule', detail: 'Linha vetorial' },
    { type: 'rect', label: 'Rectangle', icon: 'rectangle', detail: 'Retangulo' },
    { type: 'ellipse', label: 'Ellipse', icon: 'circle', detail: 'Circulo ou elipse' },
    { type: 'image', label: 'PictureBox', icon: 'image', detail: 'Imagem' },
];
const ZEBRA_TEXT_FONTS = ZEBRA_FONTS.filter((font) => font.code === '0' || /^[A-H]$/.test(font.code));
const ZEBRA_FONT_CODES = new Set(ZEBRA_FONTS.map((font) => font.code));
function buildFontOptions() {
    const standard = ZEBRA_TEXT_FONTS.map((font) => ({
        value: font.code,
        label: `${font.name}${font.scalable ? '' : ' — fixa'}`,
    }));
    const downloaded = listDownloadedFonts().map((name) => ({ value: name, label: `${name} — baixada` }));
    return [...standard, ...downloaded];
}
const RESIZE_HANDLES = [
    { id: 'nw', cursor: 'nwse-resize', style: { left: -5, top: -5 } },
    { id: 'n', cursor: 'ns-resize', style: { left: '50%', top: -5, transform: 'translateX(-50%)' } },
    { id: 'ne', cursor: 'nesw-resize', style: { right: -5, top: -5 } },
    { id: 'e', cursor: 'ew-resize', style: { right: -5, top: '50%', transform: 'translateY(-50%)' } },
    { id: 'se', cursor: 'nwse-resize', style: { right: -5, bottom: -5 } },
    { id: 's', cursor: 'ns-resize', style: { left: '50%', bottom: -5, transform: 'translateX(-50%)' } },
    { id: 'sw', cursor: 'nesw-resize', style: { left: -5, bottom: -5 } },
    { id: 'w', cursor: 'ew-resize', style: { left: -5, top: '50%', transform: 'translateY(-50%)' } },
];

function isEditableTarget(target) {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    const tagName = target.tagName.toLowerCase();
    return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
}

function roundMm(value) {
    return Math.round(value * 100) / 100;
}

function renderRulerTicks(lengthMm, pxPerMm, horizontal) {
    const count = Math.max(1, Math.floor(lengthMm));
    return Array.from({ length: count + 1 }, (_, i) => {
        const major = i % 10 === 0;
        return (
            <div
                key={i}
                className="absolute"
                style={horizontal
                    ? { left: `${i * pxPerMm}px`, top: 0, width: 1, height: major ? 10 : 5, background: major ? '#0b57d0' : 'rgba(11,87,208,0.45)' }
                    : { top: `${i * pxPerMm}px`, left: 0, height: 1, width: major ? 10 : 5, background: major ? '#0b57d0' : 'rgba(11,87,208,0.45)' }}
            >
                {major && (
                    <span className="absolute text-[7px] font-semibold leading-none text-[#0b57d0]" style={horizontal ? { left: 2, top: 1 } : { left: 2, top: 1 }}>
                        {i}
                    </span>
                )}
            </div>
        );
    });
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function snapValue(value, gridSize) {
    if (!gridSize) return roundMm(value);
    return roundMm(Math.round(value / gridSize) * gridSize);
}

function getRectPoints(rect) {
    return {
        left: rect.x,
        centerX: rect.x + rect.w / 2,
        right: rect.x + rect.w,
        top: rect.y,
        centerY: rect.y + rect.h / 2,
        bottom: rect.y + rect.h,
    };
}

function getBounds(objects) {
    if (!objects.length) return null;
    const minX = Math.min(...objects.map((item) => item.x));
    const minY = Math.min(...objects.map((item) => item.y));
    const maxX = Math.max(...objects.map((item) => item.x + item.w));
    const maxY = Math.max(...objects.map((item) => item.y + item.h));
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function getAlignmentCandidates(objects, selectedIds, canvas) {
    const selectedSet = new Set(selectedIds);
    const vertical = [
        { value: 0, source: 'canvas' },
        { value: canvas.width / 2, source: 'canvas' },
        { value: canvas.width, source: 'canvas' },
    ];
    const horizontal = [
        { value: 0, source: 'canvas' },
        { value: canvas.height / 2, source: 'canvas' },
        { value: canvas.height, source: 'canvas' },
    ];

    for (const object of objects) {
        if (selectedSet.has(object.id)) continue;
        const points = getRectPoints(object);
        vertical.push(
            { value: points.left, source: object.id },
            { value: points.centerX, source: object.id },
            { value: points.right, source: object.id },
        );
        horizontal.push(
            { value: points.top, source: object.id },
            { value: points.centerY, source: object.id },
            { value: points.bottom, source: object.id },
        );
    }

    return { vertical, horizontal };
}

function findNearestGuide(value, candidates, threshold) {
    let best = null;
    for (const candidate of candidates) {
        const delta = Math.abs(candidate.value - value);
        if (delta > threshold) continue;
        if (!best || delta < best.delta) best = { ...candidate, delta };
    }
    return best;
}

function applyGuidesToBounds(bounds, selectedIds, objects, canvas) {
    const candidates = getAlignmentCandidates(objects, selectedIds, canvas);
    const points = getRectPoints(bounds);
    const guides = { vertical: [], horizontal: [] };
    let dx = 0;
    let dy = 0;

    const verticalMatches = [
        findNearestGuide(points.left, candidates.vertical, GUIDE_THRESHOLD_MM),
        findNearestGuide(points.centerX, candidates.vertical, GUIDE_THRESHOLD_MM),
        findNearestGuide(points.right, candidates.vertical, GUIDE_THRESHOLD_MM),
    ].filter(Boolean).sort((a, b) => a.delta - b.delta);

    const horizontalMatches = [
        findNearestGuide(points.top, candidates.horizontal, GUIDE_THRESHOLD_MM),
        findNearestGuide(points.centerY, candidates.horizontal, GUIDE_THRESHOLD_MM),
        findNearestGuide(points.bottom, candidates.horizontal, GUIDE_THRESHOLD_MM),
    ].filter(Boolean).sort((a, b) => a.delta - b.delta);

    if (verticalMatches[0]) {
        const guide = verticalMatches[0];
        if (Math.abs(guide.value - points.left) <= GUIDE_THRESHOLD_MM) dx = guide.value - points.left;
        else if (Math.abs(guide.value - points.centerX) <= GUIDE_THRESHOLD_MM) dx = guide.value - points.centerX;
        else dx = guide.value - points.right;
        guides.vertical.push(roundMm(guide.value));
    }

    if (horizontalMatches[0]) {
        const guide = horizontalMatches[0];
        if (Math.abs(guide.value - points.top) <= GUIDE_THRESHOLD_MM) dy = guide.value - points.top;
        else if (Math.abs(guide.value - points.centerY) <= GUIDE_THRESHOLD_MM) dy = guide.value - points.centerY;
        else dy = guide.value - points.bottom;
        guides.horizontal.push(roundMm(guide.value));
    }

    return { dx: roundMm(dx), dy: roundMm(dy), guides };
}

function applySingleResize(object, interaction, dxPx, dyPx, canvas, gridSize, snapToGrid, pxPerMm) {
    const dx = dxPx / pxPerMm;
    const dy = dyPx / pxPerMm;
    const origin = interaction.origins[object.id];
    let x = origin.x;
    let y = origin.y;
    let w = origin.w;
    let h = origin.h;
    const right = origin.x + origin.w;
    const bottom = origin.y + origin.h;

    if (interaction.handle.includes('w')) {
        x = clamp(origin.x + dx, 0, right - MIN_OBJECT_SIZE);
        w = right - x;
    }
    if (interaction.handle.includes('e')) {
        w = clamp(origin.w + dx, MIN_OBJECT_SIZE, canvas.width - origin.x);
    }
    if (interaction.handle.includes('n')) {
        y = clamp(origin.y + dy, 0, bottom - MIN_OBJECT_SIZE);
        h = bottom - y;
    }
    if (interaction.handle.includes('s')) {
        h = clamp(origin.h + dy, MIN_OBJECT_SIZE, canvas.height - origin.y);
    }

    if (snapToGrid) {
        if (interaction.handle.includes('w')) {
            x = snapValue(x, gridSize);
            w = right - x;
        }
        if (interaction.handle.includes('e')) {
            w = snapValue(origin.x + w, gridSize) - origin.x;
        }
        if (interaction.handle.includes('n')) {
            y = snapValue(y, gridSize);
            h = bottom - y;
        }
        if (interaction.handle.includes('s')) {
            h = snapValue(origin.y + h, gridSize) - origin.y;
        }
    }

    x = clamp(x, 0, Math.max(0, canvas.width - MIN_OBJECT_SIZE));
    y = clamp(y, 0, Math.max(0, canvas.height - MIN_OBJECT_SIZE));
    w = clamp(w, MIN_OBJECT_SIZE, canvas.width - x);
    h = clamp(h, MIN_OBJECT_SIZE, canvas.height - y);

    return {
        ...object,
        x: roundMm(x),
        y: roundMm(y),
        w: roundMm(w),
        h: roundMm(h),
    };
}

function buildGuidesForResize(rect, interaction, objects, canvas) {
    const candidates = getAlignmentCandidates(objects, [interaction.primaryId], canvas);
    const points = getRectPoints(rect);
    const guides = { vertical: [], horizontal: [] };

    if (interaction.handle.includes('w')) {
        const guide = findNearestGuide(points.left, candidates.vertical, GUIDE_THRESHOLD_MM);
        if (guide) guides.vertical.push(roundMm(guide.value));
    }
    if (interaction.handle.includes('e')) {
        const guide = findNearestGuide(points.right, candidates.vertical, GUIDE_THRESHOLD_MM);
        if (guide) guides.vertical.push(roundMm(guide.value));
    }
    if (interaction.handle.includes('n')) {
        const guide = findNearestGuide(points.top, candidates.horizontal, GUIDE_THRESHOLD_MM);
        if (guide) guides.horizontal.push(roundMm(guide.value));
    }
    if (interaction.handle.includes('s')) {
        const guide = findNearestGuide(points.bottom, candidates.horizontal, GUIDE_THRESHOLD_MM);
        if (guide) guides.horizontal.push(roundMm(guide.value));
    }

    return guides;
}

function createImageObject(id) {
    return {
        id,
        type: 'image',
        x: 10,
        y: 10,
        w: 28,
        h: 20,
        src: '',
        fit: 'contain',
        borderRadius: 0,
        assetCategory: '',
        assetTags: [],
    };
}

function resolveFormatColumnWidth(format) {
    return Number(format?.columnWidthMm || format?.widthMm || 0);
}

function resolveFormatColumnHeight(format) {
    return Number(format?.columnHeightMm || format?.heightMm || 0);
}

function normalizeEditorObject(object) {
    if (!object || typeof object !== 'object') return object;
    if (object.type !== 'text' && object.type !== 'variable' && object.type !== 'date') return object;
    const normalizedFont = typeof object.font === 'string' && object.font.trim() ? object.font.trim().toUpperCase() : '0';
    return {
        ...object,
        font: ZEBRA_FONT_CODES.has(normalizedFont) || getDownloadedFont(normalizedFont) ? normalizedFont : '0',
        fontSize: typeof object.fontSize === 'number' && Number.isFinite(object.fontSize) && object.fontSize > 0 ? object.fontSize : 12,
    };
}

function normalizeContentForFormat(content, format) {
    const width = resolveFormatColumnWidth(format) || Number(content?.width) || 70;
    const height = resolveFormatColumnHeight(format) || Number(content?.height) || 35;
    return {
        ...(content || {}),
        width,
        height,
        objects: Array.isArray(content?.objects) ? content.objects.map(normalizeEditorObject) : [],
    };
}

function newObject(type, id) {
    const base = { id, type, x: 10, y: 10, w: 80, h: 12 };
    switch (type) {
        case 'text':
            return { ...base, text: 'Texto', font: '0', fontSize: 14, color: '#111111' };
        case 'variable':
            return { ...base, field: 'campo', label: 'Campo', placeholder: 'Valor do campo', font: '0', fontSize: 14, color: '#111111' };
        case 'barcode':
            return createBarcodeObject(id, 'code128');
        case 'qrcode':
            return createBarcodeObject(id, 'qrcode');
        case 'line':
            return { id, type, x: 10, y: 10, w: 30, h: 2, stroke: '#111111', strokeWidth: 1 };
        case 'rect':
            return { id, type, x: 10, y: 10, w: 30, h: 18, stroke: '#111111', strokeWidth: 1, fill: 'transparent', radius: 0 };
        case 'ellipse':
            return { id, type, x: 10, y: 10, w: 24, h: 24, stroke: '#111111', strokeWidth: 1, fill: 'transparent' };
        case 'image':
            return createImageObject(id);
        default:
            return base;
    }
}

function downloadJson(filename, data) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}

function WindowControl({ children, onClick, accent, danger = false, title }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className={`grid h-4 w-4 place-items-center rounded-[2px] border text-[10px] ${danger
                ? accent === 'blue'
                    ? 'border-white/40 bg-[#ff8e8e]/80'
                    : 'border-[#9aa2ab] bg-[#ffd3d3]'
                : accent === 'blue'
                    ? 'border-white/40 bg-white/20'
                    : 'border-[#9aa2ab] bg-white/60'
                }`}
        >
            {children}
        </button>
    );
}

function ClassicWindow({
    title,
    children,
    bodyClassName = '',
    accent = 'blue',
    action = null,
    minimized = false,
    maximized = false,
    fillHeight = false,
    onMinimize = null,
    onMaximize = null,
    onClose = null,
}) {
    const headerClassName = accent === 'blue'
        ? 'bg-[linear-gradient(180deg,#2f85ff_0%,#0b57d0_100%)] text-white'
        : 'bg-[linear-gradient(180deg,#f4f4f4_0%,#dadada_100%)] text-[#253247]';
    const hasControls = !!action || !!onMinimize || !!onMaximize || !!onClose;

    return (
        <section className={`${maximized ? 'fixed inset-[72px] z-40 flex flex-col' : ''} ${fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''} overflow-hidden rounded-[6px] border border-[#7c8694] bg-[#ece9d8] shadow-[0_8px_18px_rgba(15,23,42,0.12)]`}>
            <div className={`flex min-h-[28px] items-center justify-between gap-3 border-b border-[#7c8694] px-2 ${headerClassName}`}>
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-[15px]">window</span>
                    <span className="text-[11px] font-semibold">{title}</span>
                </div>
                {hasControls && (
                    <div className="flex items-center gap-1">
                        {action}
                        <WindowControl title={`Minimizar ${title}`} onClick={onMinimize} accent={accent}>_</WindowControl>
                        <WindowControl title={`${maximized ? 'Restaurar' : 'Maximizar'} ${title}`} onClick={onMaximize} accent={accent}>
                            {maximized ? '❐' : '□'}
                        </WindowControl>
                    </div>
                )}
            </div>
            {!minimized && <div className={`bg-[#ece9d8] ${maximized ? 'min-h-0 flex-1 overflow-auto' : ''} ${fillHeight ? 'min-h-0 flex-1' : ''} ${bodyClassName}`}>{children}</div>}
        </section>
    );
}

function MenuButton({ children, active = false, onClick = null }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-[4px] px-2 py-1 text-[11px] text-[#253247] ${active ? 'bg-white/90 shadow-[inset_0_0_0_1px_rgba(124,134,148,0.35)]' : 'hover:bg-white/80'}`}
        >
            {children}
        </button>
    );
}

function ToolbarButton({ icon, label, onClick, disabled = false, className = '', iconClassName = '', style }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={style}
            className={`flex items-center gap-2 rounded-[4px] border border-[#b9b39f] bg-[linear-gradient(180deg,#ffffff_0%,#e3dcc7_100%)] px-2 py-1 text-[10px] font-semibold text-[#253247] disabled:opacity-45 ${className}`}
        >
            <span className={`material-symbols-outlined text-[15px] ${iconClassName}`}>{icon}</span>
            {label}
        </button>
    );
}

function ToggleButton({ icon, label, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-2 rounded-[4px] border px-2 py-1 text-[10px] font-semibold ${active
                ? 'border-[#6d9df4] bg-[#dbeafe] text-[#0b57d0]'
                : 'border-[#b9b39f] bg-[linear-gradient(180deg,#ffffff_0%,#e3dcc7_100%)] text-[#253247]'
                }`}
        >
            <span className="material-symbols-outlined text-[15px]">{icon}</span>
            {label}
        </button>
    );
}

function FieldShell({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[10px] font-semibold text-[#4b5563]">{label}</span>
            {children}
        </label>
    );
}

function TextInput({ label, value, onChange, placeholder = '' }) {
    return (
        <FieldShell label={label}>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full rounded-[4px] border border-[#8f98a4] bg-white px-2 py-1.5 text-[11px] text-[#111827] outline-none focus:border-[#2f85ff]"
            />
        </FieldShell>
    );
}

function NumberField({ label, value, onChange, step = '1', disabled = false }) {
    return (
        <FieldShell label={label}>
            <input
                type="number"
                step={step}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full rounded-[4px] border border-[#8f98a4] bg-white px-2 py-1.5 text-[11px] text-[#111827] outline-none focus:border-[#2f85ff] disabled:bg-[#e5e0cc] disabled:text-[#8b8b8b]"
            />
        </FieldShell>
    );
}

function SelectField({ label, value, onChange, options }) {
    return (
        <FieldShell label={label}>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded-[4px] border border-[#8f98a4] bg-white px-2 py-1.5 text-[11px] text-[#111827] outline-none focus:border-[#2f85ff]"
            >
                {options.map((item) => (
                    <option key={item.value} value={item.value}>
                        {item.label}
                    </option>
                ))}
            </select>
        </FieldShell>
    );
}

function SectionTitle({ children }) {
    return <div className="border-b border-[#c7c0a7] pb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#475569]">{children}</div>;
}

function objectWindowTitle(selected, count) {
    if (count > 1) return 'Multi-selection properties';
    if (!selected) return 'Object properties';
    if (selected.type === 'text') return 'Label properties';
    if (selected.type === 'variable') return 'TextBox properties';
    if (selected.type === 'barcode') return 'BarCode properties';
    if (selected.type === 'qrcode') return 'Preview properties';
    if (selected.type === 'line') return 'Line properties';
    if (selected.type === 'rect') return 'Rectangle properties';
    if (selected.type === 'ellipse') return 'Ellipse properties';
    if (selected.type === 'image') return 'PictureBox properties';
    return 'Object properties';
}

export default function LabelEditor({ user }) {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const formCanvasRef = useRef(null);
    const formViewportRef = useRef(null);
    const pointerDownOnCanvasRef = useRef(false);
    const saveRef = useRef(null);
    const contentRef = useRef(null);
    const labelFormatIdRef = useRef(null);
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const lastContentRef = useRef(null);
    const isRestoringRef = useRef(false);
    const skipHistoryEffectRef = useRef(false);
    const assetParamsAppliedRef = useRef(false);
    const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';
    const [template, setTemplate] = useState(null);
    const [formats, setFormats] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [content, setContent] = useState({ width: 102, height: 152, objects: [] });
    const [pxPerMm, setPxPerMm] = useState(SCALE);
    const [selectedIds, setSelectedIds] = useState([]);
    const [primarySelectedId, setPrimarySelectedId] = useState('');
    const [labelFormatId, setLabelFormatId] = useState('');
    const [printerId, setPrinterId] = useState('');
    const [securityText, setSecurityText] = useState('{\n  "passwordProtected": false,\n  "permissions": []\n}');
    contentRef.current = content;
    labelFormatIdRef.current = labelFormatId;
    const [interaction, setInteraction] = useState(null);
    const [alignmentGuides, setAlignmentGuides] = useState({ vertical: [], horizontal: [] });
    const [snapToGrid, setSnapToGrid] = useState(true);
    const [showAlignmentGuides, setShowAlignmentGuides] = useState(true);
    const [gridSize, setGridSize] = useState(DEFAULT_GRID_SIZE);
    const [isDropActive, setIsDropActive] = useState(false);
    const [menuOpen, setMenuOpen] = useState('');
    const [windowState, setWindowState] = useState(INITIAL_WINDOW_STATE);
    const [maximizedWindowId, setMaximizedWindowId] = useState('');
    const [designerMinimized, setDesignerMinimized] = useState(false);
    const [designerMaximized, setDesignerMaximized] = useState(false);
    const [status, setStatus] = useState('');
    const [error, setError] = useState('');
    const [saveState, setSaveState] = useState('');
    const [passwordPrompt, setPasswordPrompt] = useState(false);
    const [passwordValue, setPasswordValue] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const saveResetTimerRef = useRef(null);
    const importRef = useRef(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importJsonText, setImportJsonText] = useState('');

    const windowLookup = useMemo(
        () => WINDOW_REGISTRY.reduce((accumulator, windowItem) => {
            accumulator[windowItem.id] = windowState[windowItem.id] || { hidden: false, minimized: false };
            return accumulator;
        }, {}),
        [windowState],
    );

    async function load(password = '') {
        try {
            const templateRequest = password ? api.getTemplate(id, { password }) : api.getTemplate(id);
            const [templateData, labelFormats, printers] = await Promise.all([templateRequest, api.listLabelFormats(), api.listPrinters()]);
            if (assetParamsAppliedRef.current) return;
            setPasswordPrompt(false);
            setTemplate(templateData);
            setFormats(labelFormats);
            setPrinters(printers);
            setLabelFormatId(templateData.labelFormatId || '');
            setPrinterId(templateData.printerId || '');
            setSecurityText(JSON.stringify(templateData.security ?? { passwordProtected: false, permissions: [] }, null, 2));
            const formatForContent = templateData.labelFormat || labelFormats.find((format) => format.id === templateData.labelFormatId);
            const baseContent = templateData.content?.objects?.length ? templateData.content : { width: 0, height: 0, objects: [] };
            const nextContent = normalizeContentForFormat(baseContent, formatForContent);
            skipHistoryEffectRef.current = true;
            undoStackRef.current = [];
            redoStackRef.current = [];
            lastContentRef.current = nextContent;
            setContent(nextContent);
            setSelectedIds([]);
            setPrimarySelectedId('');
        } catch (e) {
            if (e.code === 'LABEL_PASSWORD_REQUIRED') {
                setPasswordPrompt(true);
                setPasswordError(password ? 'Senha incorreta para esta etiqueta.' : '');
                return;
            }
            setError(e.message);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (skipHistoryEffectRef.current) {
            skipHistoryEffectRef.current = false;
            return;
        }
        if (isRestoringRef.current) {
            isRestoringRef.current = false;
            lastContentRef.current = content;
            return;
        }
        if (lastContentRef.current === null) return;
        if (interaction) {
            if (undoStackRef.current.length) {
                undoStackRef.current[undoStackRef.current.length - 1] = lastContentRef.current;
            }
        } else {
            undoStackRef.current.push(lastContentRef.current);
            if (undoStackRef.current.length > 50) undoStackRef.current.shift();
            redoStackRef.current = [];
        }
        lastContentRef.current = content;
    }, [content, interaction]);

    useEffect(() => {
        let lastSnapshot = '';
        const timer = setInterval(() => {
            const snapshot = `${JSON.stringify(contentRef.current)}|${labelFormatIdRef.current}`;
            if (!lastSnapshot || snapshot === lastSnapshot) {
                lastSnapshot = snapshot;
                return;
            }
            lastSnapshot = snapshot;
            saveRef.current?.(true);
        }, AUTOSAVE_INTERVAL_MS);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const handleClick = (event) => {
            if (!event.target.closest?.('[data-menu]')) setMenuOpen('');
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [menuOpen]);

    useEffect(() => {
        if (!template) return;
        const params = new URLSearchParams(location.search);
        const assetBundleKey = params.get('assetBundleKey');
        if (assetBundleKey) {
            let bundle = [];
            try {
                const raw = sessionStorage.getItem(assetBundleKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) bundle = parsed;
                }
            } catch {
                bundle = [];
            } finally {
                sessionStorage.removeItem(assetBundleKey);
            }

            if (bundle.length) {
                const insertedIds = [];
                setContent((current) => {
                    const width = Number(current.width) || 80;
                    const height = Number(current.height) || 50;
                    const cols = Math.max(1, Math.floor(width / 34));
                    const nextObjects = [...current.objects];

                    bundle.forEach((asset, index) => {
                        const col = index % cols;
                        const row = Math.floor(index / cols);
                        const imageObject = {
                            ...createImageObject(crypto.randomUUID()),
                            x: roundMm(Math.min(Math.max(6, 6 + col * 32), Math.max(6, width - 30))),
                            y: roundMm(Math.min(Math.max(6, 6 + row * 24), Math.max(6, height - 22))),
                            src: asset.assetUrl || '',
                            name: asset.assetName || 'imagem',
                            assetId: asset.assetId || '',
                            assetCategory: asset.assetCategory || '',
                            assetTags: Array.isArray(asset.assetTags) ? asset.assetTags : [],
                        };
                        insertedIds.push(imageObject.id);
                        nextObjects.push(imageObject);
                    });

                    return {
                        ...current,
                        objects: nextObjects,
                    };
                });

                if (insertedIds.length) {
                    setSelectedIds(insertedIds);
                    setPrimarySelectedId(insertedIds[insertedIds.length - 1]);
                    setStatus(`${insertedIds.length} imagem(ns) aplicadas ao layout.`);
                }
                assetParamsAppliedRef.current = true;
                navigate(location.pathname, { replace: true });
                return;
            }
        }

        const assetUrl = params.get('assetUrl');
        if (!assetUrl) return;

        const assetId = params.get('assetId') || '';
        const targetId = params.get('target') || '';
        const isReplace = params.get('replace') === '1';
        const assetName = params.get('assetName') || 'imagem';
        const assetCategory = params.get('assetCategory') || '';
        let assetTags = [];
        try {
            const parsedTags = JSON.parse(params.get('assetTags') || '[]');
            if (Array.isArray(parsedTags)) assetTags = parsedTags;
        } catch {
            assetTags = [];
        }
        let nextSelectedId = '';

        setContent((current) => {
            const targetObject = current.objects.find((object) => object.id === targetId && object.type === 'image');
            if (targetObject) {
                nextSelectedId = targetObject.id;
                return {
                    ...current,
                    objects: current.objects.map((object) => (object.id === targetObject.id
                        ? { ...object, src: assetUrl, name: assetName, assetId, assetCategory, assetTags }
                        : object)),
                };
            }

            const imageObject = {
                ...createImageObject(crypto.randomUUID()),
                x: roundMm(Math.max(6, current.width / 2 - 14)),
                y: roundMm(Math.max(6, current.height / 2 - 10)),
                src: assetUrl,
                name: assetName,
                assetId,
                assetCategory,
                assetTags,
            };
            nextSelectedId = imageObject.id;
            return {
                ...current,
                objects: [...current.objects, imageObject],
            };
        });

        if (nextSelectedId) {
            setSelectedIds([nextSelectedId]);
            setPrimarySelectedId(nextSelectedId);
        }
        setStatus(isReplace && targetId
            ? `Imagem "${assetName}" substituída mantendo o tamanho do objeto.`
            : `Imagem "${assetName}" aplicada ao layout.`);
        assetParamsAppliedRef.current = true;
        navigate(location.pathname, { replace: true });
    }, [location.pathname, location.search, navigate, template]);

    const selectedObjects = useMemo(
        () => content.objects.filter((object) => selectedIds.includes(object.id)),
        [content.objects, selectedIds],
    );
    const selected = useMemo(
        () => content.objects.find((object) => object.id === primarySelectedId) || selectedObjects[selectedObjects.length - 1] || null,
        [content.objects, primarySelectedId, selectedObjects],
    );
    const selectedBounds = useMemo(() => getBounds(selectedObjects), [selectedObjects]);
    const selectedFormat = useMemo(() => formats.find((format) => format.id === labelFormatId), [formats, labelFormatId]);
    const selectedPrinter = useMemo(() => printers.find((printer) => printer.id === printerId), [printers, printerId]);
    const selectedBarcodeDefinition = selected && isBarcodeObject(selected) ? getBarcodeDefinition(resolveBarcodeSymbology(selected)) : null;

    useEffect(() => {
        if (!interaction) return undefined;

        const previousUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';

        const handleMouseMove = (event) => {
            if (Math.hypot(event.clientX - interaction.startClientX, event.clientY - interaction.startClientY) < DRAG_THRESHOLD_PX) return;
            const dxPx = event.clientX - interaction.startClientX;
            const dyPx = event.clientY - interaction.startClientY;
            let guides = { vertical: [], horizontal: [] };

            setContent((current) => {
                const canvas = { width: current.width, height: current.height };
                if (interaction.mode === 'move') {
                    const primaryOrigin = interaction.origins[interaction.primaryId];
                    const tentativePrimary = {
                        x: primaryOrigin.x + (dxPx / pxPerMm),
                        y: primaryOrigin.y + (dyPx / pxPerMm),
                        w: primaryOrigin.w,
                        h: primaryOrigin.h,
                    };

                    const tentativeBounds = selectedBounds
                        ? {
                            x: selectedBounds.x + (dxPx / pxPerMm),
                            y: selectedBounds.y + (dyPx / pxPerMm),
                            w: selectedBounds.w,
                            h: selectedBounds.h,
                        }
                        : tentativePrimary;

                    let groupDx = dxPx / pxPerMm;
                    let groupDy = dyPx / pxPerMm;

                    if (snapToGrid) {
                        const snappedX = snapValue(tentativePrimary.x, gridSize);
                        const snappedY = snapValue(tentativePrimary.y, gridSize);
                        groupDx = snappedX - primaryOrigin.x;
                        groupDy = snappedY - primaryOrigin.y;
                    }

                    if (showAlignmentGuides && selectedBounds) {
                        const guided = applyGuidesToBounds(tentativeBounds, interaction.objectIds, current.objects, canvas);
                        groupDx += guided.dx;
                        groupDy += guided.dy;
                        guides = guided.guides;
                    }

                    const safeDxMin = Math.max(...interaction.objectIds.map((objectId) => -interaction.origins[objectId].x));
                    const safeDyMin = Math.max(...interaction.objectIds.map((objectId) => -interaction.origins[objectId].y));
                    const safeDxMax = Math.min(...interaction.objectIds.map((objectId) => canvas.width - interaction.origins[objectId].x - interaction.origins[objectId].w));
                    const safeDyMax = Math.min(...interaction.objectIds.map((objectId) => canvas.height - interaction.origins[objectId].y - interaction.origins[objectId].h));
                    groupDx = clamp(groupDx, safeDxMin, safeDxMax);
                    groupDy = clamp(groupDy, safeDyMin, safeDyMax);

                    return {
                        ...current,
                        objects: current.objects.map((object) => {
                            const origin = interaction.origins[object.id];
                            if (!origin) return object;
                            return {
                                ...object,
                                x: roundMm(origin.x + groupDx),
                                y: roundMm(origin.y + groupDy),
                            };
                        }),
                    };
                }

                const primaryObject = current.objects.find((object) => object.id === interaction.primaryId);
                if (!primaryObject) return current;

                const rect = applySingleResize(primaryObject, interaction, dxPx, dyPx, canvas, gridSize, snapToGrid, pxPerMm);
                guides = showAlignmentGuides ? buildGuidesForResize(rect, interaction, current.objects, canvas) : { vertical: [], horizontal: [] };

                return {
                    ...current,
                    objects: current.objects.map((object) => (object.id === interaction.primaryId ? rect : object)),
                };
            });

            setAlignmentGuides(guides);
        };

        const handleMouseUp = () => {
            setInteraction(null);
            setAlignmentGuides({ vertical: [], horizontal: [] });
            document.body.style.userSelect = previousUserSelect;
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = previousUserSelect;
            setAlignmentGuides({ vertical: [], horizontal: [] });
        };
    }, [gridSize, interaction, pxPerMm, selectedBounds, showAlignmentGuides, snapToGrid]);

    useEffect(() => {
        if (!selectedIds.length || !canManage) return undefined;

        const handleKeyDown = (event) => {
            if (isEditableTarget(event.target)) return;

            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
                event.preventDefault();
                if (event.shiftKey) redo();
                else undo();
                return;
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
                event.preventDefault();
                redo();
                return;
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
                event.preventDefault();
                saveRef.current?.(true);
                return;
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
                event.preventDefault();
                cloneSelected();
                return;
            }

            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                removeSelected();
                return;
            }

            const keyboardStep = event.shiftKey ? gridSize * 5 : (snapToGrid ? gridSize : 1);
            const deltaByKey = {
                ArrowLeft: { x: -keyboardStep, y: 0 },
                ArrowRight: { x: keyboardStep, y: 0 },
                ArrowUp: { x: 0, y: -keyboardStep },
                ArrowDown: { x: 0, y: keyboardStep },
            };
            const delta = deltaByKey[event.key];
            if (!delta) return;

            event.preventDefault();
            setContent((current) => {
                const selectedSet = new Set(selectedIds);
                const selectedItems = current.objects.filter((object) => selectedSet.has(object.id));
                const bounds = getBounds(selectedItems);
                if (!bounds) return current;

                const dx = clamp(delta.x, -bounds.x, current.width - bounds.x - bounds.w);
                const dy = clamp(delta.y, -bounds.y, current.height - bounds.y - bounds.h);

                return {
                    ...current,
                    objects: current.objects.map((object) => {
                        if (!selectedSet.has(object.id)) return object;
                        const nextX = object.x + dx;
                        const nextY = object.y + dy;
                        return {
                            ...object,
                            x: roundMm(snapToGrid ? snapValue(nextX, gridSize) : nextX),
                            y: roundMm(snapToGrid ? snapValue(nextY, gridSize) : nextY),
                        };
                    }),
                };
            });
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canManage, gridSize, selectedIds, snapToGrid]);

    useEffect(() => {
        if (!selectedFormat) return;
        const width = resolveFormatColumnWidth(selectedFormat);
        const height = resolveFormatColumnHeight(selectedFormat);
        if (!width || !height) return;
        setContent((current) => {
            const nextObjects = Array.isArray(current.objects) ? current.objects.map(normalizeEditorObject) : [];
            const objectsChanged = nextObjects.some((object, index) => object !== current.objects[index]);
            if (current.width === width && current.height === height && !objectsChanged) return current;
            return { ...current, width, height, objects: nextObjects };
        });
    }, [selectedFormat]);

    const fitCanvasToViewport = () => {
        const viewport = formViewportRef.current;
        if (!viewport || !content.width || !content.height) return;
        const margin = 32;
        const availW = Math.max(1, viewport.clientWidth - margin);
        const availH = Math.max(1, viewport.clientHeight - margin);
        setPxPerMm(clamp(Math.min(availW / content.width, availH / content.height), 1, 8));
    };

    useEffect(() => {
        if (!template) return;
        const timer = setTimeout(fitCanvasToViewport, 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, content.width, content.height, designerMaximized]);

    function setObject(objectId, patch) {
        setContent((current) => ({
            ...current,
            objects: current.objects.map((object) => (object.id === objectId ? normalizeEditorObject({ ...object, ...patch }) : object)),
        }));
    }

    function setObjects(objectIds, updater) {
        const selectedSet = new Set(objectIds);
        setContent((current) => ({
            ...current,
            objects: current.objects.map((object) => (selectedSet.has(object.id) ? normalizeEditorObject(updater(object)) : object)),
        }));
    }

    function selectOnly(objectId) {
        setSelectedIds([objectId]);
        setPrimarySelectedId(objectId);
    }

    function toggleSelection(objectId) {
        setSelectedIds((current) => {
            if (current.includes(objectId)) {
                const next = current.filter((idValue) => idValue !== objectId);
                if (primarySelectedId === objectId) setPrimarySelectedId(next[next.length - 1] || '');
                return next;
            }
            setPrimarySelectedId(objectId);
            return [...current, objectId];
        });
    }

    function addObject(type, placement = null) {
        const object = newObject(type, crypto.randomUUID());
        setContent((current) => {
            const nextObject = { ...object };
            if (placement) {
                const maxX = Math.max(0, current.width - nextObject.w);
                const maxY = Math.max(0, current.height - nextObject.h);
                const placedX = clamp(placement.x, 0, maxX);
                const placedY = clamp(placement.y, 0, maxY);
                nextObject.x = roundMm(snapToGrid ? snapValue(placedX, gridSize) : placedX);
                nextObject.y = roundMm(snapToGrid ? snapValue(placedY, gridSize) : placedY);
            }
            return { ...current, objects: [...current.objects, nextObject] };
        });
        selectOnly(object.id);
    }

    function handleToolboxDragStart(event, type) {
        if (!canManage) return;
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData(TOOLBOX_DRAG_MIME, type);
        event.dataTransfer.setData('text/plain', type);
    }

    function handleCanvasDragOver(event) {
        const types = Array.from(event.dataTransfer.types || []);
        const isToolboxDrag = types.includes(TOOLBOX_DRAG_MIME) || types.includes('text/plain');
        if (!isToolboxDrag) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDropActive(true);
    }

    function handleCanvasDrop(event) {
        const draggedType = event.dataTransfer.getData(TOOLBOX_DRAG_MIME) || event.dataTransfer.getData('text/plain');
        if (!draggedType || !PALETTE.some((item) => item.type === draggedType)) {
            setIsDropActive(false);
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setIsDropActive(false);

        const canvasRect = formCanvasRef.current?.getBoundingClientRect();
        if (!canvasRect) {
            addObject(draggedType);
            return;
        }

        const prototype = newObject(draggedType, crypto.randomUUID());
        const x = ((event.clientX - canvasRect.left) / pxPerMm) - (prototype.w / 2);
        const y = ((event.clientY - canvasRect.top) / pxPerMm) - (prototype.h / 2);
        addObject(draggedType, { x, y });
    }

    function setWindowMode(windowId, patch) {
        setWindowState((current) => ({
            ...current,
            [windowId]: {
                ...(current[windowId] || { hidden: false, minimized: false }),
                ...patch,
            },
        }));
    }

    function handleWindowMinimize(windowId) {
        const currentWindow = windowLookup[windowId] || { hidden: false, minimized: false };
        setWindowMode(windowId, { hidden: false, minimized: !currentWindow.minimized });
        if (maximizedWindowId === windowId) setMaximizedWindowId('');
    }

    function handleWindowMaximize(windowId) {
        setWindowMode(windowId, { hidden: false, minimized: false });
        setMaximizedWindowId((current) => (current === windowId ? '' : windowId));
    }

    function handleWindowClose(windowId) {
        setWindowMode(windowId, { hidden: true, minimized: false });
        if (maximizedWindowId === windowId) setMaximizedWindowId('');
    }

    function handleViewWindowToggle(windowId) {
        const currentWindow = windowLookup[windowId] || { hidden: false, minimized: false };
        if (currentWindow.hidden) {
            setWindowMode(windowId, { hidden: false, minimized: false });
            return;
        }
        handleWindowClose(windowId);
    }

    function restoreAllWindows() {
        setWindowState(INITIAL_WINDOW_STATE);
        setMaximizedWindowId('');
        setDesignerMinimized(false);
    }

    function getWindowProps(windowId) {
        return {
            minimized: !!windowLookup[windowId]?.minimized,
            maximized: maximizedWindowId === windowId,
            onMinimize: () => handleWindowMinimize(windowId),
            onMaximize: () => handleWindowMaximize(windowId),
            onClose: () => handleWindowClose(windowId),
        };
    }

    function handleDesignerMinimize() {
        setDesignerMinimized((current) => !current);
        if (!designerMinimized) {
            setMaximizedWindowId('');
        }
    }

    function handleDesignerMaximize() {
        setDesignerMaximized((current) => !current);
        setDesignerMinimized(false);
    }

    function undo() {
        const previous = undoStackRef.current.pop();
        if (previous === undefined) return;
        redoStackRef.current.push(contentRef.current);
        isRestoringRef.current = true;
        setContent(previous);
    }

    function redo() {
        const next = redoStackRef.current.pop();
        if (next === undefined) return;
        undoStackRef.current.push(contentRef.current);
        isRestoringRef.current = true;
        setContent(next);
    }

    function removeSelected() {
        if (!selectedIds.length) return;
        setContent((current) => ({
            ...current,
            objects: current.objects.filter((object) => !selectedIds.includes(object.id)),
        }));
        setSelectedIds([]);
        setPrimarySelectedId('');
        setAlignmentGuides({ vertical: [], horizontal: [] });
    }

    function cloneSelected() {
        const selectedSet = new Set(selectedIds);
        const originals = content.objects.filter((object) => selectedSet.has(object.id));
        if (!originals.length) return;
        const cloneIds = originals.map(() => crypto.randomUUID());
        setContent((current) => {
            let index = 0;
            const clones = current.objects
                .filter((object) => selectedSet.has(object.id))
                .map((object) => ({
                    ...object,
                    id: cloneIds[index++],
                    x: roundMm(clamp(object.x + 2, 0, Math.max(0, current.width - object.w))),
                    y: roundMm(clamp(object.y + 2, 0, Math.max(0, current.height - object.h))),
                }));
            return { ...current, objects: [...current.objects, ...clones] };
        });
        setSelectedIds(cloneIds);
        setPrimarySelectedId(cloneIds[cloneIds.length - 1] || '');
        setAlignmentGuides({ vertical: [], horizontal: [] });
    }

    const MM_PER_PX = 3.7795;
    const PT_PER_MM = 72 / 25.4;

    function measureTextWidthMm(text, fontSize, bold, font) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const fontFamily = font === 'E' ? '"OCR-B","Courier New",monospace'
            : font === 'H' || font === 'F' ? '"OCR-A","Courier New",monospace'
                : font === '0' || !font ? 'Arial, Helvetica, sans-serif'
                    : '"Courier New",monospace';
        ctx.font = `${bold ? '700' : '400'} ${fontSize}pt ${fontFamily}`;
        return ctx.measureText(String(text)).width / MM_PER_PX;
    }

    function fitSelectedToContent() {
        if (!selected || (selected.type !== 'text' && selected.type !== 'variable')) return;
        const text = selected.type === 'text'
            ? (selected.text || '')
            : (selected.placeholder || selected.label || '');
        const fontSize = selected.fontSize || 12;
        const w = roundMm(Math.max(2, measureTextWidthMm(text, fontSize, !!selected.bold, selected.font) + 1));
        const h = roundMm(Math.max(2, fontSize / PT_PER_MM + 0.6));
        setObject(selected.id, { w, h });
        setStatus(`Objeto ajustado ao conteúdo (${w} x ${h} mm).`);
    }

    function beginInteraction(event, object, mode, handle = '') {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        pointerDownOnCanvasRef.current = false;
        document.activeElement?.blur();

        const multi = event.ctrlKey || event.metaKey;
        if (multi) {
            toggleSelection(object.id);
            return;
        }

        const activeIds = selectedIds.includes(object.id) ? selectedIds : [object.id];
        setSelectedIds(activeIds);
        setPrimarySelectedId(object.id);
        if (!canManage) return;

        const origins = Object.fromEntries(
            content.objects
                .filter((item) => activeIds.includes(item.id))
                .map((item) => [item.id, { x: item.x, y: item.y, w: item.w, h: item.h }]),
        );

        setInteraction({
            mode,
            handle,
            primaryId: object.id,
            objectIds: mode === 'move' ? activeIds : [object.id],
            startClientX: event.clientX,
            startClientY: event.clientY,
            origins,
        });
    }

    function alignSelected(mode) {
        if (!selectedObjects.length) return;
        const bounds = selectedObjects.length > 1
            ? getBounds(selectedObjects)
            : { x: 0, y: 0, w: content.width, h: content.height };

        setObjects(selectedIds, (object) => {
            if (mode === 'left') return { ...object, x: roundMm(bounds.x) };
            if (mode === 'center') return { ...object, x: roundMm(bounds.x + bounds.w / 2 - object.w / 2) };
            if (mode === 'right') return { ...object, x: roundMm(bounds.x + bounds.w - object.w) };
            if (mode === 'top') return { ...object, y: roundMm(bounds.y) };
            if (mode === 'middle') return { ...object, y: roundMm(bounds.y + bounds.h / 2 - object.h / 2) };
            if (mode === 'bottom') return { ...object, y: roundMm(bounds.y + bounds.h - object.h) };
            return object;
        });
    }

    function distributeSelected(axis) {
        if (selectedObjects.length < 3) return;
        const ordered = [...selectedObjects].sort((a, b) => (axis === 'horizontal' ? a.x - b.x : a.y - b.y));
        const first = ordered[0];
        const last = ordered[ordered.length - 1];
        const occupied = ordered.reduce((sum, item) => sum + (axis === 'horizontal' ? item.w : item.h), 0);
        const span = axis === 'horizontal'
            ? (last.x + last.w) - first.x
            : (last.y + last.h) - first.y;
        const gap = (span - occupied) / (ordered.length - 1);

        let cursor = axis === 'horizontal' ? first.x : first.y;
        const nextMap = new Map();
        for (const item of ordered) {
            if (axis === 'horizontal') {
                nextMap.set(item.id, { x: roundMm(cursor) });
                cursor += item.w + gap;
            } else {
                nextMap.set(item.id, { y: roundMm(cursor) });
                cursor += item.h + gap;
            }
        }

        setObjects(selectedIds, (object) => ({ ...object, ...nextMap.get(object.id) }));
    }

    async function save(silent = false) {
        try {
            if (!silent) setSaveState('saving');
            const formatForSave = formats.find((format) => format.id === labelFormatId) || selectedFormat || template?.labelFormat || null;
            const normalizedContent = normalizeContentForFormat(content, formatForSave);
            if (normalizedContent !== content) {
                skipHistoryEffectRef.current = true;
                lastContentRef.current = normalizedContent;
                setContent(normalizedContent);
            }
            let security;
            try {
                security = JSON.parse(securityText || '{}');
            } catch {
                setError('O bloco "Segurança" precisa ser um JSON válido.');
                if (!silent) flashSaveState('error');
                return;
            }
            await api.updateTemplate(id, { content: normalizedContent, labelFormatId: labelFormatId || null, printerId: printerId || null, security });
            setStatus(silent ? 'Alterações salvas automaticamente.' : 'Layout salvo com sucesso.');
            if (!silent) flashSaveState('success');
        } catch (e) {
            setError(e.message);
            if (!silent) flashSaveState('error');
        }
    }
    saveRef.current = save;

    function flashSaveState(state) {
        setSaveState(state);
        clearTimeout(saveResetTimerRef.current);
        saveResetTimerRef.current = setTimeout(() => setSaveState(''), 2000);
    }

    useEffect(() => () => clearTimeout(saveResetTimerRef.current), []);

    function handleToolbarAction(actionId) {
        if (actionId === 'save') {
            save();
            return;
        }
        if (actionId === 'import') {
            setIsImportModalOpen(true);
            return;
        }
        if (actionId === 'delete') {
            removeSelected();
            return;
        }
        if (actionId === 'export' && template) {
            downloadJson(`${template.name.replace(/\s+/g, '-').toLowerCase()}.layout.json`, { template, content });
        }
    }

    function parseJsonLoose(text) {
        return JSON.parse(text.replace(/^\uFEFF/, '').trim());
    }

    function applyImportedObjects(raw, sourceMessage) {
        const imported = raw?.template?.content || raw;
        const objects = Array.isArray(imported?.objects) ? imported.objects : [];
        if (!objects.length) {
            setError('O JSON não contém objetos para adicionar ao layout.');
            return;
        }
        const nextObjects = objects.map((object) => {
            const normalized = normalizeEditorObject(object);
            return normalized && typeof normalized === 'object'
                ? { ...normalized, id: normalized.id || crypto.randomUUID() }
                : normalized;
        });
        setIsImportModalOpen(false);
        setImportJsonText('');
        setContent((current) => ({ ...current, objects: nextObjects }));
        setSelectedIds([]);
        setPrimarySelectedId('');
        setStatus(sourceMessage);
    }

    async function importLayoutFile(file) {
        if (!file) return;
        setError('');
        setStatus('');
        try {
            const raw = parseJsonLoose(await file.text());
            applyImportedObjects(raw, `Arquivo "${file.name}" importado para o layout.`);
        } catch {
            setError('Não foi possível importar o arquivo. Verifique o JSON.');
        }
    }

    function handleImportFromText() {
        setError('');
        setStatus('');
        try {
            const raw = parseJsonLoose(importJsonText);
            applyImportedObjects(raw, 'Objetos importados para o layout.');
        } catch {
            setError('O JSON colado é inválido.');
        }
    }

    const saveButtonStyle = saveState === 'success'
        ? { background: '#16a34a', borderColor: '#15803d', color: '#ffffff' }
        : saveState === 'error'
            ? { background: '#dc2626', borderColor: '#b91c1c', color: '#ffffff' }
            : undefined;

    const menus = [
        {
            label: 'File',
            items: [
                { type: 'action', label: 'Salvar', shortcut: 'Ctrl+S', disabled: !canManage, onClick: save },
                { type: 'action', label: 'Exportar layout', disabled: !template, onClick: () => handleToolbarAction('export') },
                { type: 'sep' },
                { type: 'action', label: 'Fechar editor', onClick: () => navigate('/templates') },
            ],
        },
        {
            label: 'Edit',
            items: [
                { type: 'action', label: 'Desfazer', shortcut: 'Ctrl+Z', disabled: undoStackRef.current.length === 0, onClick: undo },
                { type: 'action', label: 'Refazer', shortcut: 'Ctrl+Y', disabled: redoStackRef.current.length === 0, onClick: redo },
                { type: 'sep' },
                { type: 'action', label: 'Clonar selecionados', shortcut: 'Ctrl+D', disabled: !canManage || selectedIds.length === 0, onClick: cloneSelected },
                { type: 'action', label: 'Excluir selecionados', disabled: !canManage || selectedIds.length === 0, onClick: removeSelected },
            ],
        },
        {
            label: 'View',
            items: [
                ...WINDOW_REGISTRY.map((windowItem) => ({
                    type: 'toggle',
                    label: windowItem.title,
                    checked: !(windowLookup[windowItem.id]?.hidden),
                    onClick: () => handleViewWindowToggle(windowItem.id),
                })),
                { type: 'sep' },
                { type: 'action', label: 'Restaurar todas as janelas', onClick: restoreAllWindows },
                { type: 'sep' },
                { type: 'toggle', label: 'Snap to grid', checked: snapToGrid, onClick: () => setSnapToGrid((value) => !value) },
                { type: 'toggle', label: 'Guias de alinhamento', checked: showAlignmentGuides, onClick: () => setShowAlignmentGuides((value) => !value) },
            ],
        },
        {
            label: 'Layout',
            items: [
                ...ALIGN_ACTIONS.map((item) => ({ type: 'action', label: item.label, disabled: selectedIds.length === 0, onClick: () => alignSelected(item.id) })),
                { type: 'sep' },
                ...DISTRIBUTE_ACTIONS.map((item) => ({ type: 'action', label: item.label, disabled: selectedIds.length < 3, onClick: () => distributeSelected(item.id) })),
            ],
        },
        {
            label: 'Help',
            items: [
                { type: 'action', label: 'Central de Ajuda', onClick: () => navigate('/ajuda') },
            ],
        },
    ];

    if (passwordPrompt) {
        return (
            <div className="mx-auto mt-16 w-full max-w-md rounded-[8px] border border-[#7c8694] bg-[#ece9d8] shadow-[0_18px_46px_rgba(15,23,42,0.14)]">
                <div className="flex items-center gap-2 border-b border-[#7c8694] bg-[linear-gradient(180deg,#2f85ff_0%,#0b57d0_100%)] px-3 py-2 text-white">
                    <span className="material-symbols-outlined text-[15px]">lock</span>
                    <span className="text-[11px] font-semibold">Formato protegido por senha</span>
                </div>
                <div className="space-y-3 p-4">
                    <p className="text-[11px] leading-5 text-[#334155]">
                        Este formato de etiqueta exige senha para abrir. Informe a senha para continuar a edição.
                    </p>
                    {passwordError && <p className="rounded-[4px] border border-[#d7a3a3] bg-[#fff0f0] px-3 py-2 text-[10px] text-[#8b1e1e]">{passwordError}</p>}
                    <input
                        type="password"
                        autoFocus
                        value={passwordValue}
                        onChange={(e) => setPasswordValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') load(passwordValue);
                        }}
                        placeholder="Senha do formato"
                        className="w-full rounded-[4px] border border-[#8f98a4] bg-white px-3 py-2 text-[12px] text-[#111827] outline-none focus:border-[#2f85ff]"
                    />
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => navigate('/templates')}
                            className="rounded-[4px] border border-[#b9b39f] bg-white px-3 py-1.5 text-[10px] font-semibold text-[#253247]"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={() => load(passwordValue)}
                            className="rounded-[4px] border border-[#6d9df4] bg-[#dbeafe] px-3 py-1.5 text-[10px] font-semibold text-[#0b57d0]"
                        >
                            Abrir
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (error) return <p className="text-[11px] text-error">{error}</p>;
    if (!template) return <p className="text-[11px] text-on-surface-variant">Carregando...</p>;

    return (
        <div className="space-y-4">
            {status && <div className="rounded-[6px] border border-[#b9b39f] bg-[#fffbe8] px-3 py-2 text-[10px] text-[#5b4620]">{status}</div>}

            {(designerMaximized || maximizedWindowId) && <div className="fixed inset-0 z-30 bg-[#0f172a]/20" />}

            <div className={`${designerMaximized ? 'fixed inset-0 z-[60] flex flex-col' : ''} overflow-hidden rounded-[8px] border border-[#7c8694] bg-[#d6d3c7] shadow-[0_18px_46px_rgba(15,23,42,0.14)]`}>
                <div className="flex items-center justify-between gap-3 border-b border-[#7c8694] bg-[linear-gradient(180deg,#2f85ff_0%,#0b57d0_100%)] px-3 py-2 text-white">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[16px]">design_services</span>
                        <span className="text-[11px] font-semibold">Form Designer - {template.name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px]">
                        <button
                            type="button"
                            title="Salvar etiqueta"
                            onClick={save}
                            disabled={!canManage || saveState === 'saving'}
                            style={saveButtonStyle}
                            className={`mr-1 flex items-center gap-1 rounded-[4px] border border-white/40 bg-white/20 px-2 py-1 font-semibold text-white hover:bg-white/40 disabled:opacity-45 ${saveState === 'saving' ? 'animate-pulse' : ''}`}
                        >
                            <span className={`material-symbols-outlined text-[14px] ${saveState === 'saving' ? 'animate-spin' : ''}`}>
                                {saveState === 'saving' ? 'progress_activity' : 'save'}
                            </span>
                            Salvar
                        </button>
                        <WindowControl title="Minimizar designer" onClick={handleDesignerMinimize} accent="blue">_</WindowControl>
                        <WindowControl title={designerMaximized ? 'Restaurar designer' : 'Maximizar designer'} onClick={handleDesignerMaximize} accent="blue">
                            {designerMaximized ? '❐' : '□'}
                        </WindowControl>
                        <WindowControl title="Fechar designer" onClick={() => navigate('/templates')} accent="blue" danger>×</WindowControl>
                    </div>
                </div>

                {!designerMinimized && (
                    <>
                        <div className="border-b border-[#b8b1a0] bg-[#ece9d8] px-3 py-1">
                            <div className="flex flex-wrap items-center gap-1">
                                {menus.map((menu) => (
                                    <div key={menu.label} data-menu className="relative">
                                        <MenuButton active={menuOpen === menu.label} onClick={() => setMenuOpen((current) => (current === menu.label ? '' : menu.label))}>
                                            {menu.label}
                                        </MenuButton>
                                        {menuOpen === menu.label && (
                                            <div className="absolute left-0 top-full z-20 mt-1 min-w-[200px] rounded-[6px] border border-[#b8b1a0] bg-[#f8f5e8] p-2 shadow-[0_12px_24px_rgba(15,23,42,0.18)]">
                                                <div className="space-y-1">
                                                    {menu.items.map((item, index) => item.type === 'sep' ? (
                                                        <div key={`sep-${index}`} className="my-1 border-t border-[#d2ccb7]" />
                                                    ) : (
                                                        <button
                                                            key={`${item.label}-${index}`}
                                                            type="button"
                                                            disabled={item.disabled}
                                                            onClick={() => {
                                                                item.onClick?.();
                                                                setMenuOpen('');
                                                            }}
                                                            className="flex w-full items-center justify-between gap-4 rounded-[4px] px-2 py-1 text-left text-[10px] text-[#253247] hover:bg-white disabled:opacity-45"
                                                        >
                                                            <span className="flex items-center gap-2">
                                                                {item.type === 'toggle' && (
                                                                    <span className="material-symbols-outlined text-[14px]">{item.checked ? 'check_box' : 'check_box_outline_blank'}</span>
                                                                )}
                                                                {item.label}
                                                            </span>
                                                            {item.shortcut && <span className="text-[9px] text-[#64748b]">{item.shortcut}</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border-b border-[#b8b1a0] bg-[#e5dfcc] px-3 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                                {TOOLBAR_ACTIONS.map((item) => {
                                    const isSave = item.id === 'save';
                                    return (
                                        <ToolbarButton
                                            key={item.id}
                                            icon={isSave && saveState === 'saving' ? 'progress_activity' : item.icon}
                                            label={item.label}
                                            onClick={() => handleToolbarAction(item.id)}
                                            disabled={!canManage || (isSave && saveState === 'saving')}
                                            className={isSave && saveState === 'saving' ? 'animate-pulse' : ''}
                                            iconClassName={isSave && saveState === 'saving' ? 'animate-spin' : ''}
                                            style={isSave ? saveButtonStyle : undefined}
                                        />
                                    );
                                })}
                                <ToolbarButton
                                    icon="delete"
                                    label="Excluir"
                                    onClick={removeSelected}
                                    disabled={!canManage || selectedIds.length === 0}
                                />
                                <ToolbarButton
                                    icon="content_copy"
                                    label="Clonar"
                                    onClick={cloneSelected}
                                    disabled={!canManage || selectedIds.length === 0}
                                />
                                <div className="mx-1 h-6 w-px bg-[#b8b1a0]" />
                                <ToolbarButton
                                    icon="undo"
                                    label="Desfazer"
                                    onClick={undo}
                                    disabled={undoStackRef.current.length === 0}
                                />
                                <ToolbarButton
                                    icon="redo"
                                    label="Refazer"
                                    onClick={redo}
                                    disabled={redoStackRef.current.length === 0}
                                />
                                <div className="mx-1 h-6 w-px bg-[#b8b1a0]" />
                                <ToggleButton icon="grid_on" label="Snap" active={snapToGrid} onClick={() => setSnapToGrid((value) => !value)} />
                                <ToggleButton icon="align_horizontal_center" label="Guias" active={showAlignmentGuides} onClick={() => setShowAlignmentGuides((value) => !value)} />
                                <div className="mx-1 h-6 w-px bg-[#b8b1a0]" />
                                {ALIGN_ACTIONS.map((item) => (
                                    <ToolbarButton
                                        key={item.id}
                                        icon={item.icon}
                                        label={item.label}
                                        onClick={() => alignSelected(item.id)}
                                        disabled={selectedIds.length === 0}
                                    />
                                ))}
                                {DISTRIBUTE_ACTIONS.map((item) => (
                                    <ToolbarButton
                                        key={item.id}
                                        icon={item.icon}
                                        label={item.label}
                                        onClick={() => distributeSelected(item.id)}
                                        disabled={selectedIds.length < 3}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className={`${designerMaximized ? 'min-h-0 flex-1 grid-rows-[minmax(0,1fr)]' : 'xl:h-[calc(100vh-210px)] xl:grid-rows-[minmax(0,1fr)]'} grid gap-3 bg-[#d6d3c7] p-3 xl:grid-cols-[220px_minmax(0,1fr)_340px]`}>
                            <div className="studio-props-scroll min-h-0 space-y-3 overflow-y-auto">
                                {!windowLookup.toolbox.hidden && (
                                    <ClassicWindow title="ToolBox" {...getWindowProps('toolbox')}>
                                        <div className="studio-props-scroll max-h-[300px] overflow-y-auto p-2">
                                            <div className="rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-1 text-[10px] font-semibold text-[#475569]">Controls</div>
                                            <div className="mt-2 space-y-1">
                                                {PALETTE.map((item) => (
                                                    <button
                                                        key={item.type}
                                                        type="button"
                                                        draggable={canManage}
                                                        disabled={!canManage}
                                                        onClick={() => addObject(item.type)}
                                                        onDragStart={(event) => handleToolboxDragStart(event, item.type)}
                                                        onDragEnd={() => setIsDropActive(false)}
                                                        className="flex w-full items-center gap-2 rounded-[4px] border border-transparent px-2 py-1 text-left text-[11px] text-[#1f2937] hover:border-[#9bbaf7] hover:bg-[#dbeafe] disabled:opacity-45"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px] text-[#0b57d0]">{item.icon}</span>
                                                        <div>
                                                            <div className="font-semibold">{item.label}</div>
                                                            <div className="text-[9px] text-[#64748b]">{item.detail}</div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </ClassicWindow>
                                )}

                                {!windowLookup.objects.hidden && (
                                    <ClassicWindow title="Objects" accent="gray" {...getWindowProps('objects')}>
                                        <div className="studio-props-scroll max-h-[280px] overflow-auto p-2">
                                            {content.objects.length === 0 ? (
                                                <div className="rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#64748b]">
                                                    Nenhum objeto no formulario.
                                                </div>
                                            ) : (
                                                <div className="space-y-1">
                                                    {content.objects.map((object, index) => {
                                                        const active = selectedIds.includes(object.id);
                                                        return (
                                                            <button
                                                                key={object.id}
                                                                type="button"
                                                                onClick={(event) => {
                                                                    if (event.ctrlKey || event.metaKey) toggleSelection(object.id);
                                                                    else selectOnly(object.id);
                                                                }}
                                                                className={`flex w-full items-center justify-between rounded-[4px] border px-2 py-1.5 text-[10px] ${active
                                                                    ? 'border-[#6d9df4] bg-[#dbeafe] text-[#0f172a]'
                                                                    : 'border-[#c7c0a7] bg-white text-[#334155]'
                                                                    }`}
                                                            >
                                                                <span>{index + 1}. {object.type}</span>
                                                                <span>{Math.round(object.x)}:{Math.round(object.y)}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </ClassicWindow>
                                )}

                                {!windowLookup.formSetup.hidden && (
                                    <ClassicWindow title="Form setup" accent="gray" {...getWindowProps('formSetup')}>
                                        <div className="space-y-2 p-2">
                                            <NumberField label="Width (mm)" value={selectedFormat ? resolveFormatColumnWidth(selectedFormat) : content.width} onChange={(value) => setContent({ ...content, width: value })} disabled={!!selectedFormat} />
                                            <NumberField label="Height (mm)" value={selectedFormat ? resolveFormatColumnHeight(selectedFormat) : content.height} onChange={(value) => setContent({ ...content, height: value })} disabled={!!selectedFormat} />
                                            <NumberField label="Grid (mm)" value={gridSize} onChange={(value) => setGridSize(Math.max(1, value || DEFAULT_GRID_SIZE))} />
                                            <SelectField
                                                label="Format"
                                                value={labelFormatId}
                                                onChange={setLabelFormatId}
                                                options={[
                                                    { value: '', label: 'Sem formato vinculado' },
                                                    ...formats.map((format) => ({ value: format.id, label: format.name })),
                                                ]}
                                            />
                                            <SelectField
                                                label="Printer"
                                                value={printerId}
                                                onChange={setPrinterId}
                                                options={[
                                                    { value: '', label: 'Sem impressora vinculada' },
                                                    ...printers.map((printer) => ({ value: printer.id, label: `${printer.name} • ${printer.dpi} DPI` })),
                                                ]}
                                            />
                                            <label className="block">
                                                <span className="mb-1 block text-[10px] font-semibold text-[#4b5563]">Segurança / permissões (JSON)</span>
                                                <textarea
                                                    value={securityText}
                                                    onChange={(e) => setSecurityText(e.target.value)}
                                                    rows={5}
                                                    className="w-full rounded-[4px] border border-[#8f98a4] bg-white px-2 py-1.5 font-mono text-[10px] text-[#111827] outline-none focus:border-[#2f85ff]"
                                                />
                                            </label>
                                            <label className="flex items-center gap-2 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                <input type="checkbox" checked={snapToGrid} onChange={(event) => setSnapToGrid(event.target.checked)} />
                                                Snap to grid ativo
                                            </label>
                                            <label className="flex items-center gap-2 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                <input type="checkbox" checked={showAlignmentGuides} onChange={(event) => setShowAlignmentGuides(event.target.checked)} />
                                                Guias de alinhamento ativas
                                            </label>
                                        </div>
                                    </ClassicWindow>
                                )}
                            </div>

                            {!windowLookup.form.hidden && (
                                <ClassicWindow
                                    title={`${template.name} • ${selectedPrinter ? `${selectedPrinter.name} (${selectedPrinter.dpi || 203} DPI)` : '203 DPI'}`}
                                    bodyClassName="p-0"
                                    {...getWindowProps('form')}
                                    fillHeight
                                    action={(
                                        <div className="flex items-center gap-1 text-[10px]">
                                            <button
                                                type="button"
                                                title="Diminuir zoom"
                                                onClick={() => setPxPerMm((value) => Math.max(1, +(value - 0.5).toFixed(1)))}
                                                className="grid h-4 w-4 place-items-center rounded-[2px] border border-white/40 bg-white/20 text-[11px] font-bold text-white hover:bg-white/40"
                                            >
                                                −
                                            </button>
                                            <span className="min-w-[36px] text-center font-semibold">{Math.round((pxPerMm / SCALE) * 100)}%</span>
                                            <button
                                                type="button"
                                                title="Aumentar zoom"
                                                onClick={() => setPxPerMm((value) => Math.min(8, +(value + 0.5).toFixed(1)))}
                                                className="grid h-4 w-4 place-items-center rounded-[2px] border border-white/40 bg-white/20 text-[11px] font-bold text-white hover:bg-white/40"
                                            >
                                                +
                                            </button>
                                        </div>
                                    )}
                                >
                                    <div
                                        ref={formViewportRef}
                                        className="h-full min-h-0 overflow-auto bg-[linear-gradient(180deg,#f3f3f3_0%,#dbdbdb_100%)] p-4"
                                    >
                                        <div className="relative m-auto shrink-0">
                                            {showAlignmentGuides && (
                                                <div
                                                    className="pointer-events-none absolute -top-4 left-0 z-10 h-4 border-b border-[#c7c0a7] bg-white/85"
                                                    style={{ width: `${content.width * pxPerMm}px` }}
                                                >
                                                    {renderRulerTicks(content.width, pxPerMm, true)}
                                                </div>
                                            )}
                                            {showAlignmentGuides && (
                                                <div
                                                    className="pointer-events-none absolute -left-4 top-0 z-10 w-4 border-r border-[#c7c0a7] bg-white/85"
                                                    style={{ height: `${content.height * pxPerMm}px` }}
                                                >
                                                    {renderRulerTicks(content.height, pxPerMm, false)}
                                                </div>
                                            )}
                                            <div
                                                ref={formCanvasRef}
                                                className="relative border border-[#95a0ad] bg-white shadow-[0_10px_28px_rgba(15,23,42,0.12)]"
                                                style={{
                                                    width: `${content.width * pxPerMm}px`,
                                                    height: `${content.height * pxPerMm}px`,
                                                    backgroundImage: snapToGrid
                                                        ? `linear-gradient(to right, rgba(11,87,208,0.16) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,87,208,0.16) 1px, transparent 1px)`
                                                        : 'none',
                                                    backgroundSize: `${gridSize * pxPerMm}px ${gridSize * pxPerMm}px`,
                                                    boxShadow: isDropActive ? '0 0 0 2px rgba(11, 87, 208, 0.45), 0 10px 28px rgba(15,23,42,0.12)' : '0 10px 28px rgba(15,23,42,0.12)',
                                                }}
                                                onDragEnter={handleCanvasDragOver}
                                                onDragOver={handleCanvasDragOver}
                                                onDragLeave={(event) => {
                                                    if (event.currentTarget.contains(event.relatedTarget)) return;
                                                    setIsDropActive(false);
                                                }}
                                                onDrop={handleCanvasDrop}
                                                onMouseDown={() => {
                                                    pointerDownOnCanvasRef.current = true;
                                                }}
                                                onClick={() => {
                                                    if (!pointerDownOnCanvasRef.current) return;
                                                    pointerDownOnCanvasRef.current = false;
                                                    setSelectedIds([]);
                                                    setPrimarySelectedId('');
                                                    setAlignmentGuides({ vertical: [], horizontal: [] });
                                                }}
                                            >
                                                <div style={{ position: 'absolute', inset: 0 }}>
                                                    <LabelPreview content={content} scale={pxPerMm / 3.7795} dpi={selectedPrinter?.dpi || 203} />
                                                </div>

                                                {snapToGrid && (
                                                    <div
                                                        className="pointer-events-none absolute inset-0"
                                                        style={{
                                                            backgroundImage: `linear-gradient(to right, rgba(11,87,208,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,87,208,0.18) 1px, transparent 1px)`,
                                                            backgroundSize: `${gridSize * pxPerMm}px ${gridSize * pxPerMm}px`,
                                                        }}
                                                    />
                                                )}

                                                {alignmentGuides.vertical.map((guideX) => (
                                                    <div
                                                        key={`v-${guideX}`}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            bottom: 0,
                                                            left: `${guideX * pxPerMm}px`,
                                                            width: '1px',
                                                            background: '#ff2d55',
                                                            zIndex: 25,
                                                            pointerEvents: 'none',
                                                        }}
                                                    />
                                                ))}
                                                {alignmentGuides.horizontal.map((guideY) => (
                                                    <div
                                                        key={`h-${guideY}`}
                                                        style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            right: 0,
                                                            top: `${guideY * pxPerMm}px`,
                                                            height: '1px',
                                                            background: '#ff2d55',
                                                            zIndex: 25,
                                                            pointerEvents: 'none',
                                                        }}
                                                    />
                                                ))}

                                                {content.objects.map((object) => {
                                                    const active = selectedIds.includes(object.id);
                                                    return (
                                                        <div
                                                            key={object.id}
                                                            onMouseDown={(event) => beginInteraction(event, object, 'move')}
                                                            onClick={(event) => event.stopPropagation()}
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${object.x * pxPerMm}px`,
                                                                top: `${object.y * pxPerMm}px`,
                                                                width: `${object.w * pxPerMm}px`,
                                                                height: `${object.h * pxPerMm}px`,
                                                                border: active ? '1px dashed #0b57d0' : '1px solid transparent',
                                                                background: active ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                                                                cursor: canManage ? 'move' : 'pointer',
                                                                overflow: 'visible',
                                                                zIndex: active ? 8 : 2,
                                                            }}
                                                        >
                                                            {active && primarySelectedId === object.id && canManage && RESIZE_HANDLES.map((handle) => (
                                                                <button
                                                                    key={handle.id}
                                                                    type="button"
                                                                    onMouseDown={(event) => beginInteraction(event, object, 'resize', handle.id)}
                                                                    onClick={(event) => event.stopPropagation()}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        width: '10px',
                                                                        height: '10px',
                                                                        border: '1px solid #0b57d0',
                                                                        background: '#ffffff',
                                                                        borderRadius: '2px',
                                                                        cursor: handle.cursor,
                                                                        ...handle.style,
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </ClassicWindow>
                            )}

                            <div className="studio-props-scroll min-h-0 space-y-3 overflow-y-auto">
                                {!windowLookup.selectionProperties.hidden && (
                                    <ClassicWindow title="Selection properties" accent="gray" {...getWindowProps('selectionProperties')}>
                                        <div className="space-y-3 p-2">
                                            {!selectedIds.length ? (
                                                <div className="rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-3 text-[10px] text-[#64748b]">
                                                    Selecione um ou mais objetos no formulario.
                                                </div>
                                            ) : (
                                                <>
                                                    <SectionTitle>Selection</SectionTitle>
                                                    <div className="grid grid-cols-[92px_1fr] gap-x-2 gap-y-1 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                        <div className="font-semibold">Items</div>
                                                        <div>{selectedIds.length}</div>
                                                        <div className="font-semibold">Primary</div>
                                                        <div>{selected?.type || '-'}</div>
                                                        <div className="font-semibold">Bounds</div>
                                                        <div>{selectedBounds ? `${roundMm(selectedBounds.w)} x ${roundMm(selectedBounds.h)}` : '-'}</div>
                                                    </div>

                                                    {selectedIds.length === 1 && selected && (
                                                        <>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <NumberField label="X (mm)" value={selected.x} onChange={(value) => setObject(selected.id, { x: value })} step="0.1" />
                                                                <NumberField label="Y (mm)" value={selected.y} onChange={(value) => setObject(selected.id, { y: value })} step="0.1" />
                                                                <NumberField label="Width" value={selected.w} onChange={(value) => setObject(selected.id, { w: value })} step="0.1" />
                                                                <NumberField label="Height" value={selected.h} onChange={(value) => setObject(selected.id, { h: value })} step="0.1" />
                                                            </div>

                                                            {canManage && (selected.type === 'text' || selected.type === 'variable') && (
                                                                <button
                                                                    type="button"
                                                                    onClick={fitSelectedToContent}
                                                                    className="w-full rounded-[4px] border border-[#9bbaf7] bg-[#dbeafe] px-3 py-2 text-[10px] font-semibold text-[#0b57d0]"
                                                                >
                                                                    Ajustar ao conteúdo
                                                                </button>
                                                            )}
                                                        </>
                                                    )}

                                                    {canManage && (
                                                        <button
                                                            type="button"
                                                            onClick={removeSelected}
                                                            className="w-full rounded-[4px] border border-[#d7a3a3] bg-[linear-gradient(180deg,#fff0f0_0%,#f7d7d7_100%)] px-3 py-2 text-[10px] font-semibold text-[#8b1e1e]"
                                                        >
                                                            Remover seleção
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </ClassicWindow>
                                )}

                                {!windowLookup.objectProperties.hidden && (
                                    <ClassicWindow title={objectWindowTitle(selected, selectedIds.length)} accent="gray" {...getWindowProps('objectProperties')}>
                                        <div className="space-y-3 p-2">
                                            {!selectedIds.length ? (
                                                <div className="rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-3 text-[10px] text-[#64748b]">
                                                    Cada elemento possui sua propria janela de propriedades. Para multiseleção, use os comandos de alinhamento e distribuição.
                                                </div>
                                            ) : selectedIds.length > 1 ? (
                                                <>
                                                    <SectionTitle>Actions</SectionTitle>
                                                    <div className="grid gap-2">
                                                        {ALIGN_ACTIONS.map((item) => (
                                                            <ToolbarButton key={item.id} icon={item.icon} label={item.label} onClick={() => alignSelected(item.id)} />
                                                        ))}
                                                        {DISTRIBUTE_ACTIONS.map((item) => (
                                                            <ToolbarButton key={item.id} icon={item.icon} label={item.label} onClick={() => distributeSelected(item.id)} disabled={selectedIds.length < 3} />
                                                        ))}
                                                    </div>
                                                </>
                                            ) : selected ? (
                                                <>
                                                    <div className="grid grid-cols-[92px_1fr] gap-x-2 gap-y-1 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                        <div className="font-semibold">Type</div>
                                                        <div>{selected.type}</div>
                                                        <div className="font-semibold">ID</div>
                                                        <div className="truncate">{selected.id}</div>
                                                    </div>

                                                    {canManage && (
                                                        <button
                                                            type="button"
                                                            onClick={removeSelected}
                                                            className="w-full rounded-[4px] border border-[#d7a3a3] bg-[linear-gradient(180deg,#fff0f0_0%,#f7d7d7_100%)] px-3 py-2 text-[10px] font-semibold text-[#8b1e1e]"
                                                        >
                                                            Excluir objeto
                                                        </button>
                                                    )}

                                                    {(selected.type === 'text' || selected.type === 'variable') && (
                                                        <>
                                                            <SectionTitle>Text</SectionTitle>
                                                            <SelectField
                                                                label="Fonte"
                                                                value={selected.font || '0'}
                                                                onChange={(value) => setObject(selected.id, isScalable(value) ? { font: value } : { font: value, bold: undefined })}
                                                                options={buildFontOptions()}
                                                            />
                                                            {selected.type === 'text' && (
                                                                <TextInput label="Caption" value={selected.text || ''} onChange={(value) => setObject(selected.id, { text: value })} />
                                                            )}
                                                            {selected.type === 'variable' && (
                                                                <>
                                                                    <TextInput label="Field" value={selected.field || ''} onChange={(value) => setObject(selected.id, { field: value })} />
                                                                    <TextInput label="Placeholder" value={selected.placeholder || ''} onChange={(value) => setObject(selected.id, { placeholder: value })} />
                                                                </>
                                                            )}
                                                            {isScalable(selected.font) ? (
                                                                <NumberField label="Font size (pt)" value={selected.fontSize || 12} onChange={(value) => setObject(selected.id, { fontSize: value })} step="0.1" />
                                                            ) : (
                                                                <FieldShell label="Font size">
                                                                    <div className="rounded-[4px] border border-[#8f98a4] bg-[#e5e0cc] px-2 py-1.5 text-[11px] text-[#8b8b8b]">
                                                                        {`${fontMatrix(selected.font)?.h ?? 0}×${fontMatrix(selected.font)?.w ?? 0} dots (fixa)`}
                                                                    </div>
                                                                </FieldShell>
                                                            )}
                                                            <FieldShell label="Color">
                                                                <input
                                                                    type="color"
                                                                    value={selected.color || '#111111'}
                                                                    onChange={(event) => setObject(selected.id, { color: event.target.value })}
                                                                    className="h-9 w-full rounded-[4px] border border-[#8f98a4] bg-white"
                                                                />
                                                            </FieldShell>
                                                            <label className="flex items-center gap-2 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                                <input type="checkbox" disabled={!isScalable(selected.font)} checked={!!selected.bold} onChange={(event) => setObject(selected.id, { bold: event.target.checked })} />
                                                                Bold{isScalable(selected.font) ? '' : ' (só escalável)'}
                                                            </label>
                                                        </>
                                                    )}

                                                    {isBarcodeObject(selected) && (
                                                        <>
                                                            <SectionTitle>Barcode</SectionTitle>
                                                            <SelectField
                                                                label="Symbology"
                                                                value={resolveBarcodeSymbology(selected)}
                                                                onChange={(value) => setObject(selected.id, applyBarcodeSymbology(selected, value))}
                                                                options={BARCODE_SYMBOLOGIES.map((item) => ({
                                                                    value: item.id,
                                                                    label: `${item.dimension} - ${item.label}`,
                                                                }))}
                                                            />
                                                            <TextInput label="Field" value={selected.field || ''} onChange={(value) => setObject(selected.id, { field: value || undefined })} placeholder="sku, gtin, lote..." />
                                                            <TextInput label="Default value" value={selected.value || ''} onChange={(value) => setObject(selected.id, { value })} placeholder={selectedBarcodeDefinition?.defaultValue || ''} />
                                                            {selectedBarcodeDefinition?.supportsText && (
                                                                <>
                                                                    <NumberField label="Text size" value={selected.textSize || 10} onChange={(value) => setObject(selected.id, { textSize: value })} step="0.1" />
                                                                    <label className="flex items-center gap-2 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                                        <input type="checkbox" checked={!!selected.showText} onChange={(event) => setObject(selected.id, { showText: event.target.checked })} />
                                                                        Show text below
                                                                    </label>
                                                                </>
                                                            )}
                                                        </>
                                                    )}

                                                    {selected.type === 'line' && (
                                                        <>
                                                            <SectionTitle>Line</SectionTitle>
                                                            <FieldShell label="Stroke">
                                                                <input
                                                                    type="color"
                                                                    value={selected.stroke || '#111111'}
                                                                    onChange={(event) => setObject(selected.id, { stroke: event.target.value })}
                                                                    className="h-9 w-full rounded-[4px] border border-[#8f98a4] bg-white"
                                                                />
                                                            </FieldShell>
                                                            <NumberField label="Stroke width" value={selected.strokeWidth || 1} onChange={(value) => setObject(selected.id, { strokeWidth: value })} step="0.1" />
                                                        </>
                                                    )}

                                                    {selected.type === 'rect' && (
                                                        <>
                                                            <SectionTitle>Rectangle</SectionTitle>
                                                            <FieldShell label="Stroke">
                                                                <input
                                                                    type="color"
                                                                    value={selected.stroke || '#111111'}
                                                                    onChange={(event) => setObject(selected.id, { stroke: event.target.value })}
                                                                    className="h-9 w-full rounded-[4px] border border-[#8f98a4] bg-white"
                                                                />
                                                            </FieldShell>
                                                            <FieldShell label="Fill">
                                                                <input
                                                                    type="color"
                                                                    value={selected.fill && selected.fill !== 'transparent' ? selected.fill : '#ffffff'}
                                                                    onChange={(event) => setObject(selected.id, { fill: event.target.value })}
                                                                    className="h-9 w-full rounded-[4px] border border-[#8f98a4] bg-white"
                                                                />
                                                            </FieldShell>
                                                            <NumberField label="Stroke width" value={selected.strokeWidth || 1} onChange={(value) => setObject(selected.id, { strokeWidth: value })} step="0.1" />
                                                            <NumberField label="Radius" value={selected.radius || 0} onChange={(value) => setObject(selected.id, { radius: value })} step="0.1" />
                                                        </>
                                                    )}

                                                    {selected.type === 'ellipse' && (
                                                        <>
                                                            <SectionTitle>Ellipse</SectionTitle>
                                                            <FieldShell label="Stroke">
                                                                <input
                                                                    type="color"
                                                                    value={selected.stroke || '#111111'}
                                                                    onChange={(event) => setObject(selected.id, { stroke: event.target.value })}
                                                                    className="h-9 w-full rounded-[4px] border border-[#8f98a4] bg-white"
                                                                />
                                                            </FieldShell>
                                                            <FieldShell label="Fill">
                                                                <input
                                                                    type="color"
                                                                    value={selected.fill && selected.fill !== 'transparent' ? selected.fill : '#ffffff'}
                                                                    onChange={(event) => setObject(selected.id, { fill: event.target.value })}
                                                                    className="h-9 w-full rounded-[4px] border border-[#8f98a4] bg-white"
                                                                />
                                                            </FieldShell>
                                                            <NumberField label="Stroke width" value={selected.strokeWidth || 1} onChange={(value) => setObject(selected.id, { strokeWidth: value })} step="0.1" />
                                                        </>
                                                    )}

                                                    {selected.type === 'image' && (
                                                        <>
                                                            <SectionTitle>Image</SectionTitle>
                                                            <div className="grid gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        await save(true);
                                                                        navigate(`/biblioteca-imagens?templateId=${id}&target=${selected.id}`);
                                                                    }}
                                                                    className="inline-flex items-center justify-center gap-2 rounded-[4px] border border-[#9bbaf7] bg-[#dbeafe] px-3 py-2 text-[10px] font-semibold text-[#0b57d0]"
                                                                >
                                                                    <span className="material-symbols-outlined text-[14px]">frame_reload</span>
                                                                    Substituir mantendo tamanho
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        await save(true);
                                                                        navigate(`/biblioteca-imagens?templateId=${id}&target=${selected.id}`);
                                                                    }}
                                                                    className="inline-flex items-center justify-center gap-2 rounded-[4px] border border-[#c7c0a7] bg-white px-3 py-2 text-[10px] font-semibold text-[#334155]"
                                                                >
                                                                    <span className="material-symbols-outlined text-[14px]">add_photo_alternate</span>
                                                                    Inserir nova da biblioteca
                                                                </button>
                                                                <TextInput label="URL da imagem" value={selected.src || ''} onChange={(value) => setObject(selected.id, { src: value })} placeholder="https://..." />
                                                            </div>
                                                            {(selected.name || selected.assetCategory || selected.assetTags?.length) && (
                                                                <div className="rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                                    {selected.name && <div><strong>Arquivo:</strong> {selected.name}</div>}
                                                                    {selected.assetCategory && <div><strong>Categoria:</strong> {selected.assetCategory}</div>}
                                                                    {!!selected.assetTags?.length && <div><strong>Tags:</strong> {selected.assetTags.join(', ')}</div>}
                                                                </div>
                                                            )}
                                                            <SelectField
                                                                label="Fit"
                                                                value={selected.fit || 'contain'}
                                                                onChange={(value) => setObject(selected.id, { fit: value })}
                                                                options={[
                                                                    { value: 'contain', label: 'Contain' },
                                                                    { value: 'cover', label: 'Cover' },
                                                                    { value: 'fill', label: 'Fill' },
                                                                ]}
                                                            />
                                                            <NumberField label="Border radius" value={selected.borderRadius || 0} onChange={(value) => setObject(selected.id, { borderRadius: value })} step="0.1" />
                                                        </>
                                                    )}
                                                </>
                                            ) : null}
                                        </div>
                                    </ClassicWindow>
                                )}

                                {!windowLookup.formProperties.hidden && (
                                    <ClassicWindow title="Form properties" accent="gray" {...getWindowProps('formProperties')}>
                                        <div className="space-y-3 p-2">
                                            <SectionTitle>Data</SectionTitle>
                                            <div className="grid grid-cols-[92px_1fr] gap-x-2 gap-y-1 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                <div className="font-semibold">Template</div>
                                                <div>{template.name}</div>
                                                <div className="font-semibold">Objects</div>
                                                <div>{content.objects.length}</div>
                                                <div className="font-semibold">Status</div>
                                                <div>{template.status}</div>
                                                <div className="font-semibold">Category</div>
                                                <div>{template.category}</div>
                                            </div>

                                            <SectionTitle>Editor</SectionTitle>
                                            <div className="grid grid-cols-[92px_1fr] gap-x-2 gap-y-1 rounded-[4px] border border-[#c7c0a7] bg-white px-2 py-2 text-[10px] text-[#334155]">
                                                <div className="font-semibold">Format</div>
                                                <div>{selectedFormat?.name || 'Livre'}</div>
                                                <div className="font-semibold">Grid</div>
                                                <div>{gridSize} mm</div>
                                                <div className="font-semibold">Snap</div>
                                                <div>{snapToGrid ? 'Ligado' : 'Desligado'}</div>
                                                <div className="font-semibold">Guias</div>
                                                <div>{showAlignmentGuides ? 'Ligadas' : 'Desligadas'}</div>
                                                <div className="font-semibold">Atalhos</div>
                                                <div>Ctrl, setas, Shift, Delete</div>
                                            </div>
                                        </div>
                                    </ClassicWindow>
                                )}
                            </div>
                        </div>

                        <div className="border-t border-[#7c8694] bg-[#ece9d8] px-3 py-2 text-[10px] text-[#334155]">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span>Form properties</span>
                                    <span>Canvas: {content.width} x {content.height} mm</span>
                                    <span>Objetos: {content.objects.length}</span>
                                    <span>Seleção: {selectedIds.length}</span>
                                    <span>Grid: {gridSize} mm</span>
                                    {selectedBounds && <span>Bounds: {roundMm(selectedBounds.w)} x {roundMm(selectedBounds.h)} mm</span>}
                                </div>
                                <div>
                                    {interaction
                                        ? interaction.mode === 'move'
                                            ? `Movendo ${interaction.objectIds.length} objeto(s)...`
                                            : `Redimensionando ${interaction.primaryId}`
                                        : selectedIds.length > 1
                                            ? 'Ctrl alterna seleção • alinhamento e distribuição ativos'
                                            : selected
                                                ? `Selecionado: ${selected.type} • setas movem • Shift acelera • Delete remove`
                                                : 'Nenhum objeto selecionado'}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <Modal
                    open={isImportModalOpen}
                    onClose={() => {
                        setIsImportModalOpen(false);
                        setImportJsonText('');
                    }}
                    eyebrow="Importação"
                    title="Importar objetos JSON"
                    maxWidth="max-w-4xl"
                >
                    <p className="mb-5 text-[11px] leading-6 text-on-surface-variant">
                        Importe os objetos do layout a partir de um arquivo `.json` ou do conteúdo colado. Aceita o objeto exportado (com a chave content) ou apenas o objeto de layout.
                    </p>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <section className="rounded-[24px] border border-surface-container-high bg-surface-container-low p-5">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Arquivo</div>
                            <h3 className="mt-2 text-[13px] font-semibold text-on-surface">Selecionar `.json`</h3>
                            <p className="mt-2 text-[11px] leading-6 text-on-surface-variant">
                                Use esta opção quando o layout já estiver salvo em arquivo.
                            </p>
                            <button
                                type="button"
                                onClick={() => importRef.current?.click()}
                                className="mt-4 rounded-2xl bg-primary px-4 py-3 text-[11px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]"
                            >
                                Escolher arquivo JSON
                            </button>
                            <input ref={importRef} type="file" accept=".json" className="hidden" onChange={(e) => importLayoutFile(e.target.files?.[0])} />
                        </section>

                        <section className="rounded-[24px] border border-surface-container-high bg-surface-container-low p-5">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Conteúdo</div>
                            <h3 className="mt-2 text-[13px] font-semibold text-on-surface">Colar JSON</h3>
                            <p className="mt-2 text-[11px] leading-6 text-on-surface-variant">
                                Cole aqui o objeto completo com os objetos do layout para carregar sem salvar um arquivo antes.
                            </p>
                            <textarea
                                value={importJsonText}
                                onChange={(e) => setImportJsonText(e.target.value)}
                                rows={12}
                                placeholder={`{\n  "template": {\n    "content": {\n      "width": 34,\n      "height": 50,\n      "objects": []\n    }\n  }\n}`}
                                className="mt-4 w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 font-mono text-[10px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                            <button
                                type="button"
                                onClick={handleImportFromText}
                                className="mt-4 rounded-2xl border border-primary/20 bg-primary-container/60 px-4 py-3 text-[11px] font-semibold text-on-primary-container"
                            >
                                Carregar JSON colado
                            </button>
                        </section>
                    </div>
                </Modal>
            </div>
        </div>
    );
}
